/**
 * Base class for theme-aware web components
 * Provides standardized CSS variable inheritance from theme.json
 *
 * @package Felix
 * @since 1.0.0
 *
 * @example
 * import { BaseComponent } from '../shared/base-component.js'
 *
 * class MyComponent extends BaseComponent {
 *   render() {
 *     this.shadowRoot.innerHTML = `
 *       <style>
 *         :host {
 *           ${this.getThemeStyles()}
 *         }
 *       </style>
 *     `
 *   }
 * }
 */

export class BaseComponent extends HTMLElement {
	/**
	 * Returns CSS variable declarations for Shadow DOM
	 * Maps theme.json variables to component-scoped vars
	 *
	 * @returns {string} CSS variable declarations
	 */
	getThemeStyles() {
		return `
			/* Spacing scale */
			--spacing-4: var(--wp--preset--spacing--4, 0.25rem);
			--spacing-8: var(--wp--preset--spacing--8, 0.5rem);
			--spacing-12: var(--wp--preset--spacing--12, 0.75rem);
			--spacing-16: var(--wp--preset--spacing--16, 1rem);
			--spacing-24: var(--wp--preset--spacing--24, 1.5rem);
			--spacing-32: var(--wp--preset--spacing--32, 2rem);
			--spacing-38: var(--wp--preset--spacing--38, 2.375rem);
			--spacing-48: var(--wp--preset--spacing--48, 3rem);

			/* Color palette (Slate scale: 05 = darkest, 100 = lightest) */
			--color-white: var(--wp--preset--color--white, #FFFFFF);
			--color-black: var(--wp--preset--color--black, #000000);

			/* Slate scale - common values */
			--color-slate-05: var(--wp--preset--color--slate-05, #111111);
			--color-slate-10: var(--wp--preset--color--slate-10, #1C1B18);
			--color-slate-20: var(--wp--preset--color--slate-20, #31302F);
			--color-slate-25: var(--wp--preset--color--slate-25, #3C3B3A);
			--color-slate-40: var(--wp--preset--color--slate-40, #5F5E5A);
			--color-slate-50: var(--wp--preset--color--slate-50, #787774);
			--color-slate-60: var(--wp--preset--color--slate-60, #91908F);
			--color-slate-90: var(--wp--preset--color--slate-90, #E2E2E1);
			--color-slate-95: var(--wp--preset--color--slate-95, #F1F1F1);
			--color-slate-98: var(--wp--preset--color--slate-98, #FBFAF9);
			--color-slate-100: var(--wp--preset--color--slate-100, #FFFFFF);

			/* Blue scale */
			--color-blue-05: var(--wp--preset--color--blue-05, #030C17);
			--color-blue-10: var(--wp--preset--color--blue-10, #05192E);
			--color-blue-40: var(--wp--preset--color--blue-40, #1f64ad);
			--color-blue-95: var(--wp--preset--color--blue-95, #E8F0FD);
			--color-blue-98: var(--wp--preset--color--blue-98, #F5F9FE);

			/* Yellow scale */
			--color-yellow-95: var(--wp--preset--color--yellow-95, #FDF2B7);
			--color-yellow-98: var(--wp--preset--color--yellow-98, #FFF9DF);

			/* Orange scale (custom) */
			--color-orange-40: var(--wp--preset--color--orange-40, #D14010);
			--color-orange-50: var(--wp--preset--color--orange-50, #de781f);

			/* Brown scale (custom) */
			--color-brown-10: var(--wp--preset--color--brown-10, #40231a);

			/* Beige scale (custom) */
			--color-beige-90: var(--wp--preset--color--beige-90, #dcdace);
			--color-beige-95: var(--wp--preset--color--beige-95, #f6f2ed);
			--color-beige-98: var(--wp--preset--color--beige-98, #f6f5f1);

			/* Deprecated aliases (for backward compatibility) */
			--color-primary: var(--wp--preset--color--slate-20, #31302F);
			--color-primary-subtle: var(--wp--preset--color--slate-40, #5F5E5A);
			--color-border-slate: var(--wp--preset--color--slate-90, #E2E2E1);
			--color-border-gray: var(--color-border-slate); /* Legacy alias */

			/* Typography */
			--font-matter: var(--wp--preset--font-family--matter, 'Matter', sans-serif);
			/* Mobile-first to match typography.css behavior */
			--font-size-sm: var(--wp--preset--font-size--body-sm-mobile, var(--wp--preset--font-size--body-sm));
			--font-size-md: var(--wp--preset--font-size--body-md-mobile, var(--wp--preset--font-size--body-md));
			--font-size-h1: var(--wp--preset--font-size--heading-1-mobile, var(--wp--preset--font-size--heading-1));
			--font-size-h2: var(--wp--preset--font-size--heading-2-mobile, var(--wp--preset--font-size--heading-2));
			--font-size-h3: var(--wp--preset--font-size--heading-3-mobile, var(--wp--preset--font-size--heading-3));
			--font-size-h4: var(--wp--preset--font-size--heading-4-mobile, var(--wp--preset--font-size--heading-4));
			--font-size-h5: var(--wp--preset--font-size--heading-5-mobile, var(--wp--preset--font-size--heading-5));
			--font-size-h6: var(--wp--preset--font-size--heading-6-mobile, var(--wp--preset--font-size--heading-6));

			/* Desktop token aliases for component-level media queries */
			--font-size-sm-desktop: var(--wp--preset--font-size--body-sm);
			--font-size-md-desktop: var(--wp--preset--font-size--body-md);
			--font-size-h1-desktop: var(--wp--preset--font-size--heading-1);
			--font-size-h2-desktop: var(--wp--preset--font-size--heading-2);
			--font-size-h3-desktop: var(--wp--preset--font-size--heading-3);
			--font-size-h4-desktop: var(--wp--preset--font-size--heading-4);
			--font-size-h5-desktop: var(--wp--preset--font-size--heading-5);
			--font-size-h6-desktop: var(--wp--preset--font-size--heading-6);

			/* Transitions */
			--transition-fast: 0.15s ease;
		`
	}
}
